/* FLYT Rings custom band live preview (v4, 8-frame turntable on a lofted surface).
   Paints the customer's engraving onto photos of the blank ring, eight photos per size taken 45 degrees apart
   (flyt-base-<size>-f0..f7.jpg). Dragging turns the ring: the engraving follows the finger continuously and the
   photo snaps to the nearest 45 degree frame with a short cross-fade; on release the engraving settles onto that
   frame. Each frame carries its own measured rim geometry. Config lives in the template block (JSON):
     frames[size] = [url f0 .. url f7]   (or images[size] = one url for the old single-photo mode)
     geo[size]    = { f0: {top, bottom}, ... }   (or {top, bottom} for single-photo mode)
   Band style: a select whose option text is "TOP / BOTTOM (note)"; the note is dropped, a part reading
   "No top line" (or "No bottom line") is blank, and the option named in cfg.customValue reveals two free-text inputs.
   Easter egg: cfg.easterEgg = {match, html} shows a note when the number equals match.
   Focus mode (phones, cfg.focusMode !== false): while the engraving field has focus the element gets the class
   flyt-preview--focus (CSS shrinks the stage), the non-field blocks between the preview and the focused field are hidden (v4.1: heading and chips; the three lines stay), and the page
   scrolls so the ring sits right above the field over the keyboard.
   Geometry per size (v3, lofted surface): two measured ellipses as fractions of the image width (images are square):
     top    = {cx, yc, rx, ry}  the top rim, where the text area starts
     bottom = {cx, yc, rx, ry}  the bottom rim, where it ends
   The band surface is the loft between them: P(f,t) = (cx(f) + rx(f) sin t, yc(f) + ry(f) cos t), with f = 0 at the
   top rim and 1 at the bottom (parameters interpolated linearly) and t = angle around the ring (0 = facing the camera).
   Each glyph is drawn with the affine [dP/du, dP/dv], the surface tangents around the ring and down the band, so rows
   lower on the band bow more, as a close camera sees them. The old {cx, yc, r, sinp, bandh, taper} form still works. */
(function () {
  if (customElements.get('flyt-preview')) return;

  class FlytPreview extends HTMLElement {
    connectedCallback() {
      const cfgEl = this.querySelector('script[type="application/json"]');
      if (!cfgEl) return;
      try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
      const c = this.cfg;
      if (!c.frames && c.images) { c.frames = {}; Object.keys(c.images).forEach((k) => { c.frames[k] = [c.images[k]]; }); }
      if (!c.frames || !Object.keys(c.frames).length) { this.hidden = true; return; }
      this.hidden = false;
      this.imgs = [...this.querySelectorAll('img')];
      if (this.imgs.length < 2) { const i2 = this.imgs[0].cloneNode(false); i2.removeAttribute('src'); i2.alt = ''; this.imgs[0].after(i2); this.imgs.push(i2); }
      this.img = this.imgs[0]; this.imgs[1].classList.add('flyt-preview__img--next'); this.imgs[1].style.opacity = '0';
      this.frame = 0;
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.hint = this.querySelector('.flyt-preview__hint');
      this.egg = this.querySelector('.flyt-preview__egg');
      this.topText = c.topRow || ''; this.bottomText = c.bottomRow || '';
      this.text = '';
      this.theta0 = 0;
      this.size = null;
      this.loaded = {};
      const fonts = document.fonts ? Promise.all([
        document.fonts.load(`40px "${c.largeFont}"`).catch(() => {}),
        c.smallFont ? document.fonts.load(`20px "${c.smallFont}"`).catch(() => {}) : Promise.resolve(),
      ]) : Promise.resolve();
      fonts.then(() => {
        this.bindInput();
        this.bindStyle();
        this.bindSize();
        this.bindDrag();
        this.setSize(this.detectSize() || c.defaultSize || Object.keys(c.frames)[0]);
        if ('ResizeObserver' in window) new ResizeObserver(() => this.draw()).observe(this);
      });
    }

    /* ---- inputs ---- */
    bindInput() {
      this.input = document.querySelector(this.cfg.inputSelector);
      if (!this.input) return;
      const sync = () => { this.text = (this.input.value || '').toUpperCase(); this.draw(); };
      this.input.addEventListener('input', sync);
      this.input.addEventListener('change', sync);
      // focus mode (phones): while the customer types, shrink the ring and keep it just above the field so the
      // engraving stays in view over the keyboard; nothing floats or pops, the same element gets smaller in place
      this.bindFocus(this.input);
      sync();
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', () => { this.bindInput(); this.draw(); });
      }
    }
    // any of the three text fields puts the preview in focus mode; leaving one for another keeps it
    bindFocus(el) {
      if (!el || el.flytFocusBound) return; el.flytFocusBound = true;
      el.addEventListener('focus', () => this.enterFocus());
      el.addEventListener('blur', () => setTimeout(() => { const a = document.activeElement; if (!(a && a.matches && a.matches('custom-product-field input'))) this.exitFocus(); }, 60));
    }
    isPhone() { return window.matchMedia && window.matchMedia('(max-width: 749px)').matches; }
    // hide the blocks between the preview and the field being typed in that are not text fields (the heading, the
    // legend chips, the note), so on a phone it is the ring plus all three lines above the keyboard (Ken, Oct 30:
    // the three lines stay, the chips go). Switching fields re-tucks up to the new one.
    tuckFor(fieldEl) {
      const host = fieldEl.closest('custom-product-field') || fieldEl.parentElement;
      if (this.tuckHost === host) return;
      (this.tucked || []).forEach((t) => { t[0].style.display = t[1]; });
      this.tucked = []; this.tuckHost = host;
      let el = this.nextElementSibling, n = 0;
      for (; el && !el.contains(host) && el !== host && n < 12; el = el.nextElementSibling, n++) {
        if (/^(script|style)$/i.test(el.tagName) || el.hidden) continue;
        if (el.querySelector('input:not([type=hidden]), textarea')) continue;   // keep the text fields
        this.tucked.push([el, el.style.display]); el.style.display = 'none';
      }
    }
    enterFocus() {
      if (!this.isPhone() || this.cfg.focusMode === false) return;
      const a0 = document.activeElement;
      const field = (a0 && a0.matches && a0.matches('custom-product-field input')) ? a0 : this.input;
      this.classList.add('flyt-preview--focus');
      this.tuckFor(field);
      const place = () => {
        const stage = this.querySelector('.flyt-preview__stage') || this;
        // leave room for a sticky header (Shrine wraps the header section in position: sticky)
        let off = 0;
        for (let n = document.querySelector('sticky-header, .header-wrapper, .shopify-section-header'); n && n !== document.body; n = n.parentElement) {
          const pos = getComputedStyle(n).position;
          if (pos === 'sticky' || pos === 'fixed') off = Math.max(off, n.getBoundingClientRect().height);
        }
        let top = stage.getBoundingClientRect().top + window.scrollY - off - 8;
        // keep the focused field above the keyboard; if the ring and the field do not both fit, the field wins
        const a = document.activeElement;
        if (a && a.matches && a.matches('custom-product-field input')) {
          const avail = (window.visualViewport ? window.visualViewport.height : window.innerHeight);
          const fb = a.getBoundingClientRect().bottom + window.scrollY;
          if (fb - top > avail - 12) top = fb - avail + 12;
        }
        window.scrollTo({ top, behavior: 'smooth' });
      };
      clearTimeout(this.focusT); this.focusT = setTimeout(place, 260);
      if (window.visualViewport && !this.vvBound) { this.vvBound = true; window.visualViewport.addEventListener('resize', () => { if (this.classList.contains('flyt-preview--focus')) { clearTimeout(this.focusT); this.focusT = setTimeout(place, 120); } }); }
    }
    exitFocus() {
      if (!this.classList.contains('flyt-preview--focus')) return;
      this.classList.remove('flyt-preview--focus');
      (this.tucked || []).forEach((t) => { t[0].style.display = t[1]; }); this.tucked = []; this.tuckHost = null;
      clearTimeout(this.focusT);
      setTimeout(() => this.draw(), 220);
    }
    bindStyle() {
      const c = this.cfg;
      if (!c.styleSelector && !c.customTopSelector) return;
      const sel = c.styleSelector ? document.querySelector(c.styleSelector) : null;
      const topIn = c.customTopSelector ? document.querySelector(c.customTopSelector) : null;
      const botIn = c.customBottomSelector ? document.querySelector(c.customBottomSelector) : null;
      const host = (el) => el && (el.closest('custom-product-field') || el.parentElement);
      const apply = () => {
        const raw = sel ? (sel.value || '') : '';
        // no dropdown on the page (Oct 25 layout): the two inputs are the legend, always shown
        const isCustom = !sel || (c.customValue && raw.toLowerCase().indexOf(c.customValue.toLowerCase()) === 0);
        if (sel && host(topIn)) host(topIn).hidden = !isCustom;
        if (sel && host(botIn)) host(botIn).hidden = !isCustom;
        if (isCustom) {
          [topIn, botIn].forEach((el) => { if (el && el.value !== el.value.toUpperCase()) { const p = el.selectionStart; el.value = el.value.toUpperCase(); try { el.setSelectionRange(p, p); } catch (e) {} } });
          this.topText = (topIn && topIn.value || '');
          this.bottomText = (botIn && botIn.value || '');
        } else {
          if (topIn) topIn.value = '';                        // keep stray custom text out of the order
          if (botIn) botIn.value = '';
          const legend = raw.replace(/\s*\([^)]*\)\s*$/, '').trim();   // drop the trailing note
          const parts = legend.split(/\s+\/\s+/).map((p) => (/^(no (top|bottom) line|none|blank)$/i.test(p.trim()) ? '' : p));
          this.topText = (parts[0] || '').toUpperCase();
          this.bottomText = (parts[1] || '').toUpperCase();
        }
        this.draw();
      };
      if (sel) sel.addEventListener('change', apply);
      [topIn, botIn].forEach((el) => { if (el) { el.addEventListener('input', apply); el.addEventListener('change', apply); this.bindFocus(el); } });
      apply();
    }
    detectSize() {
      const keys = Object.keys(this.cfg.frames);
      const norm = (v) => (v || '').toString().trim().replace(/\s+/g, '');
      const match = (v) => keys.find((k) => norm(k) === norm(v)) || keys.find((k) => norm(v).indexOf(norm(k)) >= 0);
      let found = null;
      document.querySelectorAll('variant-selects input[type=radio]:checked, variant-radios input[type=radio]:checked, .product-form__input input[type=radio]:checked, fieldset input[type=radio]:checked').forEach((el) => { if (!found) found = match(el.value); });
      if (!found) document.querySelectorAll('variant-selects select, variant-selects option:checked, .product-form__input select').forEach((el) => { if (!found) found = match(el.value); });
      if (!found) {
        const url = new URL(window.location.href);
        const vid = url.searchParams.get('variant');
        if (vid && this.cfg.variantSizes && this.cfg.variantSizes[vid]) found = match(this.cfg.variantSizes[vid]);
      }
      return found;
    }
    bindSize() {
      const onChange = () => { const s = this.detectSize(); if (s && s !== this.size) this.setSize(s); };
      document.addEventListener('change', onChange, true);
      document.addEventListener('click', () => setTimeout(onChange, 50), true);
      window.addEventListener('popstate', onChange);
    }
    setSize(key) {
      const c = this.cfg;
      if (!c.frames[key]) return;
      this.size = key;
      this.theta0 = 0; this.frame = 0;
      const list = c.frames[key];
      if (list.length > 1) list.forEach((u) => { const im = new Image(); im.src = u; });   // warm the cache for the spin
      this.applyGeo();
      this.showFrame(0, false);
    }
    nFrames() { return (this.cfg.frames[this.size] || []).length || 1; }
    applyGeo() {
      const c = this.cfg, g = c.geo || {}, gs = g[this.size] || {};
      const fk = gs['f' + this.frame] || gs;                    // per-frame geometry, or one set for the size
      this.geo = Object.assign({}, g._default || {}, fk.top ? fk : gs);
    }
    // show frame k: swap the photo, cross-fading when the ring is turning
    showFrame(k, fade) {
      const list = this.cfg.frames[this.size]; if (!list) return;
      k = ((k % list.length) + list.length) % list.length;
      const src = list[k];
      this.frame = k; this.applyGeo();
      if (this.img.getAttribute('src') === src) { this.draw(); return; }
      if (fade && this.imgs.length > 1) {
        const nxt = this.imgs[1 - this.imgs.indexOf(this.img)], cur = this.img;
        let done = false;
        const go = () => {
          if (done || nxt.getAttribute('src') !== src) return; done = true; nxt.onload = null;
          nxt.style.transition = 'opacity 140ms ease'; nxt.style.opacity = '1';
          cur.style.transition = 'opacity 140ms ease'; cur.style.opacity = '0';
          this.img = nxt; this.draw();
        };
        if (nxt.getAttribute('src') !== src) nxt.setAttribute('src', src);
        if (nxt.complete && nxt.naturalWidth) go(); else nxt.onload = go;
      } else {
        this.img.onload = () => this.draw();
        this.img.setAttribute('src', src);
        if (this.img.complete && this.img.naturalWidth) this.draw();
      }
    }
    // keep the engraving angle and the photo frame in step
    syncFrame(fade) {
      const n = this.nFrames(); if (n < 2) return;
      const step = 2 * Math.PI / n;
      const k = Math.round(this.theta0 / step);
      if (k !== this.frame) this.showFrame(k, fade);
    }
    bindDrag() {
      if (this.cfg.rotate === false) return;
      let x0 = null, t0 = 0, moved = false, lastTap = 0;
      const el = this.canvas;
      el.style.touchAction = 'pan-y';
      el.addEventListener('pointerdown', (e) => { x0 = e.clientX; t0 = this.theta0; moved = false; this.stopAnim(); el.setPointerCapture(e.pointerId); if (this.classList.contains('flyt-preview--focus')) e.preventDefault(); });
      el.addEventListener('pointermove', (e) => {
        if (x0 == null) return;
        const W = this.img.clientWidth || 1;
        const r = ((this.geo.top && this.geo.top.rx) || this.geo.r || 0.29) * W;
        const n = this.nFrames();
        const th = t0 + (e.clientX - x0) / r;
        this.theta0 = n > 1 ? th : Math.max(-Math.PI, Math.min(Math.PI, th));   // frames: keep turning; one photo: half a turn
        if (Math.abs(e.clientX - x0) > 12) { moved = true; if (this.hint) this.hint.hidden = true; }
        this.syncFrame(true);
        this.draw();
      });
      const up = () => {
        if (x0 == null) return; x0 = null;
        const n = this.nFrames();
        if (n > 1) { const step = 2 * Math.PI / n; this.animateTo(Math.round(this.theta0 / step) * step); }
        if (!moved) { const now = Date.now(); if (now - lastTap < 350) this.reset(); lastTap = now; }
      };
      el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
      el.addEventListener('dblclick', () => this.reset());
    }
    reset() { this.stopAnim(); this.theta0 = 0; this.showFrame(0, true); this.draw(); }
    stopAnim() { if (this.anim) { cancelAnimationFrame(this.anim); this.anim = null; } }
    animateTo(target) {
      this.stopAnim();
      const from = this.theta0, d = target - from, dur = 180, t1 = performance.now();
      if (Math.abs(d) < 1e-4) { this.theta0 = target; this.syncFrame(true); this.draw(); return; }
      const tick = (now) => {
        const p = Math.min(1, (now - t1) / dur), e = 1 - (1 - p) * (1 - p);
        this.theta0 = from + d * e;
        this.syncFrame(true); this.draw();
        this.anim = p < 1 ? requestAnimationFrame(tick) : null;
      };
      this.anim = requestAnimationFrame(tick);
    }

    /* ---- drawing ---- */
    draw() {
      if (!this.img || !this.img.complete || !this.img.naturalWidth || !this.geo) return;
      const c = this.cfg, W = this.img.clientWidth, H = this.img.clientHeight;
      if (!W || !H) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      if (this.canvas.width !== Math.round(W * dpr) || this.canvas.height !== Math.round(H * dpr)) {
        this.canvas.width = Math.round(W * dpr); this.canvas.height = Math.round(H * dpr);
      }
      this.canvas.style.width = W + 'px'; this.canvas.style.height = H + 'px';
      const ctx = this.ctx;
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.dpr = dpr;
      const g = this.geo;
      let top, bot;
      if (g.top && g.bottom) {
        top = { cx: g.top.cx * W, yc: g.top.yc * W, rx: g.top.rx * W, ry: g.top.ry * W };
        bot = { cx: g.bottom.cx * W, yc: g.bottom.yc * W, rx: g.bottom.rx * W, ry: g.bottom.ry * W };
      } else {                                           // legacy cylinder form
        const cosp = Math.sqrt(1 - g.sinp * g.sinp), taper = g.taper || 0;
        top = { cx: g.cx * W, yc: g.yc * W, rx: g.r * W, ry: g.r * g.sinp * W };
        bot = { cx: g.cx * W, yc: g.yc * W + g.bandh * W * cosp, rx: g.r * (1 - taper) * W, ry: g.r * (1 - taper) * g.sinp * W };
      }
      this.G = { top, bot, bandh: Math.hypot(bot.cx - top.cx, (bot.yc + bot.ry) - (top.yc + top.ry)) };
      // Layout reference: every frame of a size shares one text layout (glyph size, spacing, angle of each letter),
      // measured on the size's f0 geometry, so the engraving does not creep or rescale as the ring turns. Only the
      // projection onto the band (surf) uses the current frame's rims.
      const gs = (c.geo || {})[this.size] || {}, g0 = gs.f0 && gs.f0.top ? gs.f0 : (g.top ? g : null);
      if (g0 && g0.top && g0.bottom) {
        const t0 = { cx: g0.top.cx * W, yc: g0.top.yc * W, rx: g0.top.rx * W, ry: g0.top.ry * W };
        const b0 = { cx: g0.bottom.cx * W, yc: g0.bottom.yc * W, rx: g0.bottom.rx * W, ry: g0.bottom.ry * W };
        this.Gref = { top: t0, bot: b0, bandh: Math.hypot(b0.cx - t0.cx, (b0.yc + b0.ry) - (t0.yc + t0.ry)) };
      } else this.Gref = this.G;

      const text = this.text || (c.placeholder || '');
      const ghost = !this.text;
      const rows = c.rows;
      const jobs = [];
      if (this.topText && rows.top) jobs.push([this.topText, rows.top, c.smallFont || c.largeFont, false]);
      // the ghost prompt (no text yet) is laid out exactly like a real number, so it wraps and aligns the same way
      if (text && rows.middle) jobs.push([text, rows.middle, c.largeFont, ghost]);
      if (this.bottomText && rows.bottom) jobs.push([this.bottomText, rows.bottom, c.smallFont || c.largeFont, false]);
      // alignRows: every row spans the same arc, so the first and last letters of all three rows line up
      // (as the laser lays them out). The longest row sets the span; shorter rows open their tracking to match.
      let span = null;
      if (c.alignRows !== false && jobs.length > 1) {
        span = Math.max.apply(null, jobs.map((j) => this.measureRow(ctx, j[0], j[1], j[2]).span));
      }
      jobs.forEach((j) => this.row(ctx, j[0], j[1], j[2], j[3], span));
      if (this.egg && c.easterEgg) {
        const hit = this.text && this.text.replace(/\s+/g, '') === String(c.easterEgg.match || '').toUpperCase().replace(/\s+/g, '');
        if (hit && !this.egg.innerHTML) this.egg.innerHTML = c.easterEgg.html;
        this.egg.hidden = !hit;
      }
      if (c.debug) this.guides(ctx);
      this.classList.toggle('flyt-preview--empty', ghost);
    }

    // surface point and tangents at band depth f (0 top rim .. 1 bottom rim) and angle t
    surf(f, t) {
      const T = this.G.top, B = this.G.bot;
      const cx = T.cx + f * (B.cx - T.cx), yc = T.yc + f * (B.yc - T.yc);
      const rx = T.rx + f * (B.rx - T.rx), ry = T.ry + f * (B.ry - T.ry);
      const sT = Math.sin(t), cT = Math.cos(t);
      return {
        x: cx + rx * sT, y: yc + ry * cT, rx, ry,
        // d/dt (around the ring) and d/df (down the band)
        tx: rx * cT, ty: -ry * sT,
        fx: (B.cx - T.cx) + (B.rx - T.rx) * sT, fy: (B.yc - T.yc) + (B.ry - T.ry) * cT,
      };
    }

    // natural size of a row: glyph widths, tracking, arc span in radians (capped at maxDeg)
    measureRow(ctx, text, row, font) {
      const c = this.cfg, G = this.Gref || this.G;
      const cap = row.cap * G.bandh;
      const size = cap / 0.70;                           // FLYT Band: cap height = 0.70 em
      const xs = row.xscale != null ? row.xscale : (c.xscale || 1);
      const tracking = (row.tracking != null ? row.tracking : 0.1) * size;
      const R = G.top.rx + row.h * (G.bot.rx - G.top.rx);   // reference half-width of the band at this row
      ctx.font = `${size}px "${font}"`;
      const chars = [...text];
      const widths = chars.map((ch) => ctx.measureText(ch).width * xs);
      const glyphs = widths.reduce((a, b) => a + b, 0);
      const total = glyphs + tracking * Math.max(0, chars.length - 1);
      const maxArc = (row.maxDeg || c.maxDeg || 300) * Math.PI / 180;
      return { cap, size, xs, tracking, R, chars, widths, glyphs, total, span: Math.min(total / R, maxArc), maxArc };
    }

    // row: { h (fraction of the band height from the top rim to the row's vertical center), cap (fraction of band height), tracking (em), xscale }
    // span: optional target arc (radians) the row must fill, from alignRows
    row(ctx, text, row, font, ghost, span) {
      const c = this.cfg, G = this.Gref || this.G, dpr = this.dpr;
      const m = this.measureRow(ctx, text, row, font);
      const { cap, size, xs, R, chars, widths } = m;
      let tracking = m.tracking, scale = 1;
      const f = row.h;
      ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'center';
      if (span != null && chars.length > 1) {
        const want = span * R;                           // arc length this row should cover
        if (want > m.total) tracking = (want - m.glyphs) / (chars.length - 1);   // open the letter spacing to match
      }
      const adv = widths.map((w) => w + tracking);
      const total = adv.reduce((a, b) => a + b, 0) - tracking;
      const maxArc = m.maxArc * R;                       // never let a row wrap onto itself
      if (total > maxArc) scale = maxArc / total;

      const ink = c.ink || '#111111';
      const strokeEm = (row.stroke != null ? row.stroke : (c.stroke != null ? c.stroke : 0.09));
      let theta = this.theta0 - (total * scale) / (2 * R);
      const stripRad = (c.stripDeg || 3) * Math.PI / 180;    // each glyph is drawn in vertical strips this wide (in arc)
      const alpha = ghost ? (c.ghostAlpha || 0.3) : 1;
      const res = 2;                                     // offscreen glyph resolution (x device pixels)
      // The row is first assembled on two offscreen layers the size of the canvas (opaque ink, and the sheen), strip by
      // strip, so overlapping strips never double up; the layers are then composited once: shadow, sheen, ink.
      const CW = this.canvas.width, CH = this.canvas.height;
      const rowInk = this.layer('ink', CW, CH), rowSheen = this.layer('sheen', CW, CH);
      const xi = rowInk.getContext('2d'), xsh = rowSheen.getContext('2d');
      xi.setTransform(1, 0, 0, 1, 0, 0); xi.clearRect(0, 0, CW, CH);
      xsh.setTransform(1, 0, 0, 1, 0, 0); xsh.clearRect(0, 0, CW, CH);
      let drawn = false;
      chars.forEach((ch, i) => {
        const w = widths[i] * scale;
        const tc = theta + w / (2 * R);                  // angle of the glyph's center
        if (Math.cos(tc) > (c.edgeCos || 0.1) && ch !== ' ') {
          // Curved glyph: the glyph is rendered once to a small offscreen bitmap, then pasted onto the band in vertical
          // strips, each strip projected at its own angle, so a wide digit bends with the ring instead of sitting on it
          // as a flat card.
          const L = this.glyphLayers(ch, size, font, cap, strokeEm, res * dpr);
          const nStrips = Math.max(1, Math.min(14, Math.ceil((w / R) / stripRad)));
          const sw = w / nStrips;                        // strip width (image-scaled glyph units)
          const k = L.k;                                 // layer pixels per glyph unit
          for (let j = 0; j < nStrips; j++) {
            const u0 = -w / 2 + j * sw, u1 = u0 + sw, um = (u0 + u1) / 2;
            const t = tc + um / R;                       // angle of this strip
            const ct = Math.cos(t), e0 = (c.edgeCos || 0.1), e1 = (c.edgeFade || 0.25);
            if (ct <= e0) continue;
            const fade = Math.min(1, (ct - e0) / (e1 - e0));   // strips dissolve over the last few degrees before the silhouette
            const S = this.surf(f, t);
            const a = S.tx / R * xs * scale, b = S.ty / R * xs * scale;
            const cc = S.fx / G.bandh, d = S.fy / G.bandh;
            const ug = um / scale;                       // strip center in unscaled glyph units
            // strips overlap by about one glyph unit each side so antialiased edges never leave a hairline gap
            const ov = Math.ceil(k) + 1;
            const px0 = Math.max(0, Math.round((u0 / scale + L.ox) * k) - ov), px1 = Math.min(L.w, Math.round((u1 / scale + L.ox) * k) + ov);
            if (px1 <= px0) continue;
            const gx = px0 / k - L.ox, gw = (px1 - px0) / k;   // back to glyph units
            [xi, xsh].forEach((x, n) => {
              x.globalAlpha = fade;
              x.setTransform(dpr * a, dpr * b, dpr * cc, dpr * d, dpr * (S.x - a * ug), dpr * (S.y - b * ug));
              x.drawImage(n ? L.sheen : L.ink, px0, 0, px1 - px0, L.h, gx, -L.oy, gw, L.h / k);
              x.globalAlpha = 1;
            });
            drawn = true;
          }
        }
        theta += adv[i] * scale / R;
      });
      if (drawn) {
        const sz = size * dpr;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        if (!ghost) {
          // recess shadow: the ink layer, blurred and nudged down-right
          ctx.globalCompositeOperation = 'multiply'; ctx.globalAlpha = 0.35;
          try { ctx.filter = `blur(${(sz * 0.05).toFixed(2)}px)`; } catch (e) {}
          ctx.drawImage(rowInk, sz * 0.01, sz * 0.03);
          try { ctx.filter = 'none'; } catch (e) {}
          // sheen: a light edge up-left of the stroke, as the ink gloss catches the light
          if (c.sheen !== false) { ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 0.3; ctx.drawImage(rowSheen, -sz * 0.018, -sz * 0.022); }
        }
        ctx.globalCompositeOperation = 'multiply'; ctx.globalAlpha = alpha;
        ctx.drawImage(rowInk, 0, 0);
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
    }

    // Offscreen renderings of one glyph in glyph units (origin at the glyph's center x, baseline at cap/2):
    // ink = fill + stroke with the recess shadow; sheen = the light edge. k = layer pixels per glyph unit.
    glyphLayers(ch, size, font, cap, strokeEm, k) {
      const key = [ch, Math.round(size * 100), font, Math.round(cap * 100), strokeEm, Math.round(k * 100)].join('|');
      this.glyphs = this.glyphs || new Map();
      const hit = this.glyphs.get(key);
      if (hit) return hit;
      if (this.glyphs.size > 400) this.glyphs.clear();
      const ox = size * 1.1, oy = size * 0.6, W = Math.ceil(size * 2.2 * k), H = Math.ceil(size * 2.0 * k);
      const mk = () => { const cv = document.createElement('canvas'); cv.width = W; cv.height = H; const x = cv.getContext('2d'); x.setTransform(k, 0, 0, k, ox * k, oy * k); x.font = `${size}px "${font}"`; x.textAlign = 'center'; x.textBaseline = 'alphabetic'; x.lineJoin = 'round'; x.lineCap = 'round'; return [cv, x]; };
      const [ink, xi] = mk();
      const inkColor = (this.cfg.ink || '#111111');
      xi.fillStyle = inkColor; xi.strokeStyle = inkColor; xi.lineWidth = size * strokeEm;
      xi.strokeText(ch, 0, cap / 2); xi.fillText(ch, 0, cap / 2);
      const [sheen, xs] = mk();
      xs.strokeStyle = '#ffffff'; xs.lineWidth = size * (strokeEm + 0.03);
      xs.strokeText(ch, 0, cap / 2);
      const L = { ink, sheen, ox, oy, k, h: H, w: W };
      this.glyphs.set(key, L);
      return L;
    }
    // a reusable canvas-sized offscreen layer
    layer(name, W, H) {
      this.layers = this.layers || {};
      let cv = this.layers[name];
      if (!cv) { cv = document.createElement('canvas'); this.layers[name] = cv; }
      if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
      return cv;
    }

    guides(ctx) {
      const dpr = this.dpr, rows = this.cfg.rows || {};
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 1;
      const fs = [0, 1].concat(Object.keys(rows).map((k) => rows[k].h));
      fs.forEach((f, i) => {
        ctx.strokeStyle = i < 2 ? 'rgba(255,0,0,0.8)' : 'rgba(0,160,255,0.8)';
        ctx.beginPath();
        for (let j = 0; j <= 120; j++) {
          const S = this.surf(f, -Math.PI + j * Math.PI * 2 / 120);
          j ? ctx.lineTo(S.x, S.y) : ctx.moveTo(S.x, S.y);
        }
        ctx.closePath(); ctx.stroke();
      });
      const S0 = this.surf(0, 0);
      ctx.strokeStyle = 'rgba(255,0,0,0.8)'; ctx.beginPath(); ctx.moveTo(S0.x, 0); ctx.lineTo(S0.x, this.img.clientHeight); ctx.stroke();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
  }
  customElements.define('flyt-preview', FlytPreview);
})();
